import { Component, computed, inject, OnInit, signal } from "@angular/core";
import { AuthService } from "../../../core/services/auth.service";
import { ActivatedRoute, Router } from "@angular/router";
import { getHttpErrorMessage } from "../../../shared/utils/http-error.util";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  Validators,
} from "@angular/forms";
import { AsyncPipe, CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { TuiDay, TuiValidationError } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiError,
  TuiLink,
  TuiTextfield,
  TuiLoader
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListWrapper,
  TuiFade,
  TuiFieldErrorPipe,
  TuiFiles,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiTabs,
  TuiUnfinishedValidator,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation } from "@taiga-ui/layout";
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputDateTimeModule,
  TuiInputModule,
  TuiInputNumberModule,
  TuiInputPhoneModule,
  TuiInputTimeModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { ClientService } from "../../../core/services/client.service";
import { RouterLink } from "@angular/router";
import { TuiTable } from "@taiga-ui/addon-table";
import { ProcedureRecordService } from "../../../core/services/procedure-record.service";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";
import { formatClientIdentifier } from "../../../shared/utils/client-identifier";
import { buildInvoiceChargesRouteUrl } from "../../../shared/utils/invoice-charges-route";

@Component({
  selector: "app-add-procedure",
  standalone: true,
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    CommonModule,
    TuiAppearance,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiFiles,
    TuiLoader,
    TuiFieldErrorPipe,
    TuiError,
    ReactiveFormsModule,
    AsyncPipe,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiTextareaModule,
    TuiInputPhoneModule,
    TuiComboBoxModule,
    TuiMultiSelectModule,
    TuiSelectModule,
    TuiDataListWrapper,
    TuiInputDateModule,
    TuiTable,
    TuiStringifyContentPipe,
    TuiFilterByInputPipe,
    TuiInputDateTimeModule,
    TuiInputTimeModule,

  ],
  templateUrl: "./add-procedure.component.html",
  styleUrl: "./add-procedure.component.scss",
})
export class AddProcedureComponent implements OnInit {
  alert = inject(TuiAlertService);

  isLoading = false;
  isSaving = false;
  private isProcedureLoading = false;
  private isClientsLoading = false;

  predefinedProcedures = [
    'Catheter Inserting',
    'Wound Dressing',
    'IV Injection',
    'Ambulance Service',
    'Physiotherapy',
    'Nursing Care',
    'Doctor Care'
  ];

  // Dropdown options for procedures
  procedureOptions: string[] = [...this.predefinedProcedures, 'Other'];
  showAddNewProcedure = false;
  
  // New procedure input
  newProcedure = '';

  // Custom payment types
  customPayments: { name: string; value: number | null }[] = [];
  newPaymentName = '';
  newPaymentValue: number | null = null;

  procedureForm: FormGroup;
  procedureId: string | null = null;
  isEditMode = false;
  /**
   * Walk-in mode: when true the form skips Patient Type / Client picker and
   * collects a manually entered client name + optional contact instead.
   * Toggled by routing into `/admin/walk-in-extra-charges/add-edit` (the
   * route data flag is read in `ngOnInit`).
   */
  isWalkInMode = false;

  /** Full eligible-clients list, used as the source for the type filter. */
  allClients: any[] = [];
  /** Clients shown in the picker, scoped to the chosen Patient Type. */
  clients: any[] = [];
  loggedInUserCanEdit = false;
  private preselectClientId: string | null = null;
  private returnUrl: string | null = null;

  /**
   * Patient Type drives the Client picker. Mirrors the Phase 2 Extra Charges
   * form so picking "Inward" surfaces inward patients, "Non Registered"
   * surfaces NREG-, etc. Stored as {label, value} so the combo can render a
   * friendly label while we send the canonical backend value when filtering.
   *
   * Note: "Inward" is retained here so historical extra-charge records
   * tied to Inward clients can still resolve their stored value and
   * render correctly in edit mode. Creation is restricted via
   * `selectableClientTypeOptions` below.
   */
  protected readonly clientTypeOptions: { label: string; value: string }[] = [
    { label: "Registered", value: "Registered" },
    { label: "Non Registered", value: "Unregistered" },
    { label: "Inward", value: "Inward" },
  ];

  /**
   * What the Patient Type dropdown actually shows. Inward is intentionally
   * hidden from the picker so admins no longer create Inward extra
   * charges through this form, but existing records keep working.
   */
  protected readonly selectableClientTypeOptions = this.clientTypeOptions.filter(
    (opt) => opt.value !== "Inward",
  );

  protected stringifyClientType(
    item: { label: string; value: string } | null,
  ): string {
    return item ? item.label : "";
  }

  hasFieldValue(path: string): boolean {
    const value = this.procedureForm.get(path)?.value;
    return this.hasStandaloneValue(value);
  }

  hasStandaloneValue(value: unknown): boolean {
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== undefined && value !== "";
  }
  private authService = inject(AuthService);

  constructor(
    private fb: FormBuilder,
    private clientService: ClientService,
    private alerts: TuiAlertService,
    private procedureService: ProcedureRecordService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.procedureForm = this.fb.group({
      // Default to the first option ("Registered") so the picker is non-empty
      // when the page first loads.
      clientType: [this.clientTypeOptions[0], Validators.required],
      clientID: ["", Validators.required],
      // Walk-in only: manually entered client name + optional contact. These
      // controls are only required when isWalkInMode is true (configured in
      // ngOnInit via applyWalkInValidators after route data is read).
      walkInClientName: [""],
      walkInContactNo: [""],
      procedures: [[], Validators.required],
      doctorName: [""],
      nurseName: ["", Validators.required],
      // Optional address - useful for Inward / Unregistered clients that
      // don't carry a guardianAddress on the client record itself.
      address: [""],
      payments: this.fb.group({
        doctorPayment: [null],
        nursingPayment: [null],
        pharmacyPayment: [null],
        laboratoryPayment: [null],
        ambulancePayment: [null],
        physiotherapyPayment: [null],
        otherPayment: [null]
      }),
      grandTotal: [0],
      date: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    // Route data flag is the source of truth for walk-in mode. Both the
    // dedicated walk-in routes (/admin/walk-in-extra-charges/add-edit[/:id])
    // set `data: { walkIn: true }`; the standard procedures routes leave it
    // unset so the form keeps its existing client-picker behavior.
    this.isWalkInMode = !!this.route.snapshot.data?.["walkIn"];
    this.applyWalkInValidators();

    if (!this.isWalkInMode) {
      this.loadClients();
    }
    this.checkUserPermission();
    this.setupPaymentCalculation();
    this.setupProceduresMultiSelect();
    this.preselectClientId = this.route.snapshot.queryParamMap.get("clientId");
    this.returnUrl = this.route.snapshot.queryParamMap.get("returnUrl");

    // Re-scope the client picker whenever the user changes Patient Type, and
    // clear the previously selected client because it likely belongs to the
    // old type and would otherwise look "selected but invisible".
    this.procedureForm
      .get("clientType")
      ?.valueChanges.subscribe((value: any) => {
        this.applyClientTypeFilter(value?.value);
        this.procedureForm.patchValue({ clientID: "" }, { emitEvent: false });
      });

    this.route.paramMap.subscribe((params) => {
      this.procedureId = params.get("id");
      if (this.procedureId) {
        this.isEditMode = true;
        this.loadProcedure(this.procedureId);
      }
    });
  }

  private checkLoadingState(): void {
    this.isLoading = this.isProcedureLoading || this.isClientsLoading;
  }

  /**
   * Swap validators based on walk-in vs client-linked mode. Walk-in records
   * don't carry a clientID/clientType and instead require a manually
   * entered name; everything else stays the same so the rest of the form
   * (charges, procedures, date) validates identically.
   */
  private applyWalkInValidators(): void {
    const clientTypeCtrl = this.procedureForm.get("clientType");
    const clientIDCtrl = this.procedureForm.get("clientID");
    const walkInNameCtrl = this.procedureForm.get("walkInClientName");

    if (this.isWalkInMode) {
      clientTypeCtrl?.clearValidators();
      clientIDCtrl?.clearValidators();
      walkInNameCtrl?.setValidators([Validators.required]);
      // Wipe any pre-existing client selection so it doesn't ride along
      // when the user navigates between modes in the same session.
      this.procedureForm.patchValue(
        { clientType: null, clientID: "" },
        { emitEvent: false },
      );
    } else {
      clientTypeCtrl?.setValidators([Validators.required]);
      clientIDCtrl?.setValidators([Validators.required]);
      walkInNameCtrl?.clearValidators();
    }

    clientTypeCtrl?.updateValueAndValidity({ emitEvent: false });
    clientIDCtrl?.updateValueAndValidity({ emitEvent: false });
    walkInNameCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Filter the client combo box based on the selected client type.
   * Clients without an explicit clientType (legacy rows) are treated as
   * Registered to keep them reachable.
   */
  private applyClientTypeFilter(clientTypeValue: string | undefined): void {
    const type = clientTypeValue || "Registered";
    this.clients = this.allClients.filter((c: any) => {
      const ct = c?.clientType || "Registered";
      return ct === type;
    });
  }

  private setupProceduresMultiSelect(): void {
    const ctrl = this.procedureForm.get("procedures");
    ctrl?.valueChanges.subscribe((raw) => {
      const list = Array.isArray(raw) ? raw.filter(Boolean).map((v) => String(v)) : [];

      // Remove duplicates while preserving order
      const unique: string[] = [];
      for (const item of list) {
        if (!unique.includes(item)) unique.push(item);
      }

      const hasOther = unique.includes("Other");
      const cleaned = hasOther ? unique.filter((p) => p !== "Other") : unique;

      if (hasOther) {
        this.showAddNewProcedure = true;
      }

      // Only patch back if we actually changed something (avoid loops)
      const changed =
        cleaned.length !== list.length ||
        cleaned.some((v, i) => v !== list[i]) ||
        unique.length !== list.length;

      if (changed) {
        ctrl?.setValue(cleaned, { emitEvent: false });
      }
    });
  }


  get finalProcedures(): string[] {
    return this.procedureForm.get('procedures')?.value || [];
  }



  get paymentControl(): AbstractControl | null {
    return this.procedureForm.get("payment");
  }

  get doctorNameControl(): AbstractControl | null {
    return this.procedureForm.get("doctorName");
  }

  get nurseNameControl(): AbstractControl | null {
    return this.procedureForm.get("nurseName");
  }


  loadProcedure(id: string): void {
    this.isProcedureLoading = true;
    this.isLoading = true;
    this.procedureService.getProcedureById(id).subscribe({
      next: (data: any) => {
        // tui-input-date (legacy) is bound to a TuiDay instance; passing a
        // pre-formatted string leaves the control in an invalid shape, which
        // is why the calendar wouldn't open and the field couldn't be edited.
        const tuiDay = this.toTuiDay(data.date);

        // Handle both single procedure and procedures array for backward compatibility
        const procedures = data.procedures || (data.procedure ? [data.procedure] : []);
        
        // Handle payments - default to null values if not present
        const payments = data.payments || {
          doctorPayment: null,
          nursingPayment: null,
          pharmacyPayment: null,
          laboratoryPayment: null,
          ambulancePayment: null,
          physiotherapyPayment: null,
          otherPayment: null
        };

        // Walk-in rows have no clientID; the manual name lives directly on
        // the procedure document. Detect via clientType so legacy rows that
        // somehow carry a name without the flag still load cleanly.
        const isWalkInRecord =
          data?.clientType === "WalkIn" || (!data?.clientID && !!data?.walkInClientName);

        if (isWalkInRecord) {
          // Snap into walk-in mode so validators and the template render
          // the right inputs even if the user landed via a non-walk-in route.
          this.isWalkInMode = true;
          this.applyWalkInValidators();

          this.procedureForm.patchValue(
            {
              clientType: null,
              clientID: "",
              walkInClientName: data.walkInClientName || "",
              walkInContactNo: data.walkInContactNo || "",
              procedures: procedures,
              doctorName: data.doctorName,
              nurseName: data.nurseName,
              address: data.address || "",
              payments: payments,
              grandTotal: data.grandTotal || 0,
              date: tuiDay,
            },
            { emitEvent: false },
          );
        } else {
          // The backend populates clientID on this endpoint, so it's the full
          // client document. Use it to detect Patient Type without needing to
          // wait for the parallel loadClients() call to finish.
          const populatedClient =
            data?.clientID && typeof data.clientID === "object"
              ? data.clientID
              : null;
          const detectedType =
            this.clientTypeOptions.find(
              (opt) =>
                opt.value === (populatedClient?.clientType || "Registered"),
            ) || this.clientTypeOptions[0];

          // emitEvent: false so the clientType valueChanges listener doesn't
          // wipe out the clientID we're patching in this same call.
          this.procedureForm.patchValue(
            {
              clientType: detectedType,
              clientID: populatedClient ?? data.clientID,
              procedures: procedures,
              doctorName: data.doctorName,
              nurseName: data.nurseName,
              address: data.address || "",
              payments: payments,
              grandTotal: data.grandTotal || 0,
              date: tuiDay,
            },
            { emitEvent: false },
          );

          this.applyClientTypeFilter(detectedType.value);
        }

        // Load custom payments
        this.customPayments = data.customPayments || [];
        this.isProcedureLoading = false;
        this.checkLoadingState();
      },
      error: (err) => {
        console.error("Error loading procedure:", err);
        this.alerts.open("Error loading procedure");
        this.isProcedureLoading = false;
        this.checkLoadingState();
      },
    });
  }

  private toTuiDay(dateValue: any): TuiDay | null {
    if (!dateValue) return null;
    if (dateValue instanceof TuiDay) return dateValue;
    const native = new Date(dateValue);
    if (isNaN(native.getTime())) return null;
    return TuiDay.fromLocalNativeDate(native);
  }


  loadClients(): void {
    this.isClientsLoading = true;
    this.isLoading = true;
    this.clientService.getAllClients().subscribe({
      next: (response: any) => {
        const clients = Array.isArray(response) ? response : [];
        this.allClients = clients.filter(
          (c: any) => !c?.archived && c?.active !== false,
        );
        // Re-apply the current Patient Type filter against the freshly loaded
        // list. Without this the picker stays empty until the user toggles
        // Patient Type once.
        this.applyClientTypeFilter(
          this.procedureForm.get("clientType")?.value?.value,
        );

        if (!this.isEditMode && this.preselectClientId) {
          const linked = this.allClients.find(
            (c: any) => c?._id === this.preselectClientId,
          );
          if (linked) {
            // Auto-snap Patient Type to whatever the linked client actually
            // is, so the combo shows them and the value isn't dropped.
            const detectedType =
              this.clientTypeOptions.find(
                (opt) => opt.value === (linked?.clientType || "Registered"),
              ) || this.clientTypeOptions[0];

            this.procedureForm.patchValue(
              { clientType: detectedType, clientID: linked },
              { emitEvent: false },
            );
            this.applyClientTypeFilter(detectedType.value);
          }
        }
        this.isClientsLoading = false;
        this.checkLoadingState();
      },
      error: (error) => {
        console.error("Error fetching clients:", error);
        this.isClientsLoading = false;
        this.checkLoadingState();
      },
    });
  }


stringifyClients(client: any): string {
  if (!client) return "";
  
  // Helper to append guardian name if it exists
  const formatNameWithGuardian = (c: any) => {
    const identifier = formatClientIdentifier(c);
    const clientName = c.name || "";
    // Check if guardianName exists and isn't just whitespace
    const guardian = c.guardianName?.trim() ? ` ${c.guardianName.trim()}` : "";
    
    return `${identifier} - ${guardian}`;
  };

  if (typeof client === "object") {
    return formatNameWithGuardian(client);
  }
  
  const foundClient = this.clients.find((c: any) => c._id === client);
  return foundClient ? formatNameWithGuardian(foundClient) : "";
}


  stringifyDropdown(item: { items: string; value: number } | null): string {
    if (!item) return "";
    return item.items;
  }

  addNewProcedure(): void {
    if (this.newProcedure.trim()) {
      const currentProcedures = this.procedureForm.get('procedures')?.value || [];
      if (!currentProcedures.includes(this.newProcedure.trim())) {
        this.procedureForm.patchValue({
          procedures: [...currentProcedures, this.newProcedure.trim()]
        });
      }
      
      this.newProcedure = '';
      this.showAddNewProcedure = false; // Hide the add new procedure section
      
      this.alerts
        .open("New procedure added successfully.", {
          appearance: "success",
          label: "Success",
        })
        .subscribe();
    }
  }

  removeProcedure(procedure: string): void {
    const currentProcedures = this.procedureForm.get('procedures')?.value || [];
    const updatedProcedures = currentProcedures.filter((p: string) => p !== procedure);
    this.procedureForm.patchValue({
      procedures: updatedProcedures
    });
  }

  isProcedureSelected(procedure: string): boolean {
    const currentProcedures = this.procedureForm.get('procedures')?.value || [];
    return currentProcedures.includes(procedure);
  }

  stringifyProcedure = (item: string): string => item;

  setupPaymentCalculation(): void {
    // Subscribe to changes in the payments form group
    this.procedureForm.get('payments')?.valueChanges.subscribe(() => {
      this.calculateGrandTotal();
    });
  }

  calculateGrandTotal(): void {
    const payments = this.procedureForm.get('payments')?.value || {};
    
    let total = 0;
    
    // Calculate standard payments
    Object.values(payments).forEach(value => {
      const numValue = value === null || value === '' ? 0 : Number(value);
      total += isNaN(numValue) ? 0 : numValue;
    });
    
    // Add custom payments
    this.customPayments.forEach(customPayment => {
      if (customPayment.value !== null && customPayment.value !== undefined) {
        total += Number(customPayment.value) || 0;
      }
    });
    
    this.procedureForm.patchValue({ grandTotal: total });
  }

  private hasUnsavedCustomPaymentDraft(): boolean {
    const name = (this.newPaymentName ?? "").trim();
    if (name.length > 0) return true;
    const v = this.newPaymentValue as unknown;
    if (v === null || v === undefined || v === "") return false;
    return true;
  }

  addCustomPayment(): void {
    if (this.newPaymentName.trim() && this.newPaymentValue !== null && this.newPaymentValue !== undefined) {
      this.customPayments.push({
        name: this.newPaymentName.trim(),
        value: this.newPaymentValue
      });
      
      this.newPaymentName = '';
      this.newPaymentValue = null;
      this.calculateGrandTotal();
    }
  }

  removeCustomPayment(index: number): void {
    this.customPayments.splice(index, 1);
    this.calculateGrandTotal();
  }

  updateCustomPaymentValue(index: number, value: number | null): void {
    this.customPayments[index].value = value;
    this.calculateGrandTotal();
  }

  onCustomPaymentInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value ? Number(target.value) : null;
    this.updateCustomPaymentValue(index, value);
  }

  submit(): void {
     if(!this.loggedInUserCanEdit){
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    if (this.procedureForm.invalid) {
      this.procedureForm.markAllAsTouched();
      return;
    }

    if (this.hasUnsavedCustomPaymentDraft()) {
      this.alerts
        .open(
          "Add the custom charge with the Add button, or clear the Custom charge name and Amount fields.",
          { label: "Unsaved custom charge", appearance: "error" },
        )
        .subscribe();
      return;
    }

    // Calculate grand total before submitting
    this.isSaving = true;
    this.calculateGrandTotal();

    // The combo box may bind the full client object (edit mode / preselect)
    // OR a raw id (when typed in fresh) - always send the string id to the
    // backend so Mongoose's ObjectId cast doesn't blow up.
    const rawClientID = this.procedureForm.value.clientID;
    const clientID =
      rawClientID && typeof rawClientID === "object"
        ? rawClientID._id?.toString?.() ?? rawClientID._id
        : rawClientID || undefined;

    const basePayload: any = {
      procedures: this.finalProcedures,
      doctorName: this.procedureForm.value.doctorName,
      nurseName: this.procedureForm.value.nurseName,
      address: this.procedureForm.value.address,
      payments: this.procedureForm.value.payments,
      customPayments: this.customPayments,
      grandTotal: this.procedureForm.value.grandTotal,
      date: this.procedureForm.value.date,
    };

    const payload = this.isWalkInMode
      ? {
          ...basePayload,
          // Walk-ins intentionally have no Client document; the backend
          // model accepts walk-in fields directly on the procedure record.
          clientID: null,
          clientType: "WalkIn",
          walkInClientName: this.procedureForm.value.walkInClientName?.trim() || "",
          walkInContactNo: this.procedureForm.value.walkInContactNo?.trim() || "",
        }
      : { ...basePayload, clientID };

    // Walk-ins have no client invoice page to land on; route the user back
    // to the walk-in charges list instead.
    const successFallbackUrl = this.isWalkInMode
      ? "/admin/walk-in-extra-charges"
      : "/admin/procedures";

    const navigateOnSuccess = () => {
      const invoiceUrl = this.isWalkInMode
        ? null
        : buildInvoiceChargesRouteUrl(clientID, this.procedureForm.value.date);
      this.router.navigateByUrl(
        invoiceUrl ?? this.returnUrl ?? successFallbackUrl,
      );
    };

    if (this.isEditMode && this.procedureId) {
      this.procedureService
        .updateProcedure(this.procedureId, payload)
        .subscribe({
          next: (response) => {
            this.isSaving = false;
            this.alerts
              .open("Procedure updated successfully", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            navigateOnSuccess();
          },
          error: (error) => {
            this.isSaving = false;
            this.alerts
              .open(getHttpErrorMessage(error), {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            console.error("Error updating procedure:", error);
          },
        });
    } else {
      this.procedureService.createProcedure(payload).subscribe({
        next: (response) => {
          this.isSaving = false;
          this.alerts
            .open("Procedure record created successfully", {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          navigateOnSuccess();
        },
        error: (error) => {
          this.isSaving = false;
          this.alerts
            .open(getHttpErrorMessage(error), {
              label: "Error",
              appearance: "negative",
            })
            .subscribe();
          console.error("Error creating procedure record:", error);
        },
      });
    }
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("EXTRA_CHARGES_CREATE");
  }
}
